import React, { useState } from 'react'
import { Plus } from 'lucide-react'
import {
  AddItemButton,
  Card,
  ChecklistList,
  PrimaryButton,
  SectionTitle,
  TextArea,
} from './styles'
import { ChecklistItem } from './types'
import { ApprovalApi } from './api'
import ChecklistItemRow from './ChecklistItemRow'

interface Props {
  approvalId: number
  items: ChecklistItem[]
  isAdmin: boolean
  currentUserId?: number
  locked: boolean
  onItemsChanged: (items: ChecklistItem[]) => void
}

const ChecklistSection: React.FC<Props> = ({
  approvalId,
  items,
  isAdmin,
  currentUserId,
  locked,
  onItemsChanged,
}) => {
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)

  const handleAdd = async () => {
    const description = draft.trim()
    if (!description) return
    setBusy(true)
    try {
      const created = await ApprovalApi.addChecklistItem(approvalId, {
        description,
      })
      onItemsChanged([...items, created])
      setDraft('')
      setAdding(false)
    } finally {
      setBusy(false)
    }
  }

  const handleItemChanged = (updated: ChecklistItem) => {
    onItemsChanged(items.map((i) => (i.id === updated.id ? updated : i)))
  }

  const handleItemRemoved = (id: number) => {
    onItemsChanged(items.filter((i) => i.id !== id))
  }

  return (
    <Card>
      <SectionTitle>Checklist de aprovação</SectionTitle>
      <ChecklistList>
        {items.length === 0 && (
          <div
            style={{
              padding: '20px 12px',
              fontSize: 13,
              color: '#6b7280',
              textAlign: 'center',
              border: '1px dashed #e5e7eb',
              borderRadius: 8,
            }}
          >
            Nenhum item no checklist ainda. Adicione o primeiro abaixo.
          </div>
        )}
        {items.map((item, index) => (
          <ChecklistItemRow
            key={item.id}
            item={item}
            index={index}
            isAdmin={isAdmin}
            currentUserId={currentUserId}
            locked={locked}
            onItemChanged={handleItemChanged}
            onItemRemoved={handleItemRemoved}
          />
        ))}
      </ChecklistList>

      {!locked && (
        <div style={{ marginTop: 14 }}>
          {adding ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: 14,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                background: '#f9fafb',
              }}
            >
              <TextArea
                placeholder='Descreva o item do checklist...'
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={busy}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type='button'
                  onClick={() => {
                    setAdding(false)
                    setDraft('')
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6b7280',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    padding: '8px 14px',
                  }}
                >
                  Cancelar
                </button>
                <PrimaryButton
                  type='button'
                  onClick={handleAdd}
                  disabled={busy || !draft.trim()}
                >
                  Adicionar item
                </PrimaryButton>
              </div>
            </div>
          ) : (
            <AddItemButton type='button' onClick={() => setAdding(true)}>
              <Plus size={16} /> Adicionar item ao checklist
            </AddItemButton>
          )}
        </div>
      )}
    </Card>
  )
}

export default ChecklistSection
